import type { FC } from 'react';
import { useMemo } from 'react';
import { PapiDeviation, PapiGallection } from '@wix/da-papi-types';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { useMeasureElementCookie } from '@wix/da-hooks/pkg/useMeasureElement/useMeasureElementCookie';
import { MeasuredCookieType } from '@wix/da-hooks/pkg/useMeasureElement/redux/types';
import DndSortableList from '@wix/da-shared-react/pkg/Dnd/DndSortableList';
import { DraggableType } from '@wix/da-shared-react/pkg/types/dnd';
import { useDraftsSelect } from '../../hooks/useDraftsSelect';
import { usePageOffsets } from '../../hooks/usePageOffsets';
import StudioDeviationGridItem from './StudioDeviationGridItem';

import s from './StudioDeviationGrid.scss';

const CARDS_PER_ROW = 5;
const CARD_GAP = 32;
const CARD_HEIGHT = 142;
const GRID_PADDING = 24;

export interface Props {
  deviations: PapiDeviation[];
  selectProps: ReturnType<typeof useDraftsSelect>;
  selectedFolder: PapiGallection;
  isDndReorderSupported?: boolean;
  onReorder: (folderId: number, deviationId: number, position: number) => void;
}
const StudioDeviationGrid: FC<Props> = ({
  deviations,
  selectedFolder,
  isDndReorderSupported,
  onReorder,
  selectProps: { selected, onSelect },
}) => {
  const { ref: containerRef, dimensions: measuredContainer } = useMeasureElementCookie(
    MeasuredCookieType.STUDIO_GRID,
    {
      cookieDim: 'width',
    }
  );

  const { topOfPage } = usePageOffsets(selectedFolder);

  const gridSpacing = CARD_GAP * (CARDS_PER_ROW - 1) + GRID_PADDING * 2;
  const cardWidth = (measuredContainer.width - gridSpacing) / 5;

  const sortedDeviations = useMemo(
    () =>
      deviations.map((deviation, idx) => ({
        id: deviation.deviationId,
        idx,
        deviation,
      })),
    [deviations]
  );

  const renderGridItem = (deviation, idx) => (
    <StudioDeviationGridItem
      key={deviation.deviationId}
      deviation={deviation}
      width={cardWidth}
      height={CARD_HEIGHT}
      hasSelection={selected.length > 0}
      offset={idx}
      isSelected={selected.includes(idx)}
      onSelect={onSelect}
    />
  );

  return (
    <BiLoggerContextProvider value={{ sectionname: 'studio-grid' }}>
      <div className={s['grid']} ref={containerRef}>
        {isDndReorderSupported && (
          <DndSortableList
            dndType={DraggableType.STUDIO_GRID_ITEM}
            items={sortedDeviations}
            onDragOrder={(item, position) => {
              onReorder(selectedFolder.folderId, item.deviation.deviationId, topOfPage + position);
            }}
          >
            {item => renderGridItem(item.deviation, item.idx)}
          </DndSortableList>
        )}

        {!isDndReorderSupported && deviations.map(renderGridItem)}
      </div>
    </BiLoggerContextProvider>
  );
};
StudioDeviationGrid.displayName = 'StudioDeviationGrid';

export default StudioDeviationGrid;
